import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/Header';
import { useEmployee, useDeleteEmployee } from '../hooks/useEmployees';
import type { EmploymentStatus } from '../types';
import { toast } from '../utils/toast';

export const EmployeeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { employee, loading, error } = useEmployee(id || '');
  const { deleteEmployee, loading: deleting } = useDeleteEmployee();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleEdit = () => {
    navigate(`/employees/${id}/edit`);
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!id) return;
    
    const success = await deleteEmployee(id);
    if (success) {
      toast.success('Employee deleted successfully');
      navigate('/employees');
    } else {
      toast.error('Failed to delete employee');
    }
    setShowDeleteDialog(false);
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
  };

  const getStatusBadgeColor = (status: EmploymentStatus) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'konseling':
        return 'bg-yellow-100 text-yellow-800';
      case 'sp1':
        return 'bg-orange-100 text-orange-800';
      case 'sp2':
        return 'bg-orange-200 text-orange-900';
      case 'sp3':
        return 'bg-red-100 text-red-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar />
        <Header title="Employee Detail" />
        <main className="ml-0 md:ml-64 pt-20 p-6">
          <div className="max-w-7xl mx-auto">
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading employee details...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar />
        <Header title="Employee Detail" />
        <main className="ml-0 md:ml-64 pt-20 p-6">
          <div className="max-w-7xl mx-auto">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {error || 'Employee not found'}
            </div>
            <button
              onClick={() => navigate('/employees')}
              className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Back to Employee List
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <Header title="Employee Detail" />
      
      <main className="ml-0 md:ml-64 p-4 md:px-6 w-full pt-20">
        <div className="mx-1 md:mx-6">
          {/* Header with Actions */}
          <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{employee.Nama}</h1>
              <p className="text-gray-600 mt-1">{employee.Jabatan || 'No title assigned'}</p>
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <button
                onClick={() => navigate('/employees')}
                className="flex-1 sm:flex-none px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
              >
                Back to List
              </button>
              <button
                onClick={handleEdit}
                className="flex-1 sm:flex-none px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                Edit
              </button>
              <button
                onClick={handleDeleteClick}
                className="flex-1 sm:flex-none px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
              >
                Delete
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            {/* Photo Card - Takes 1 column */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
                <div className="flex flex-col items-center">
                  {employee.Foto ? (
                    <img
                      src={employee.Foto}
                      alt={employee.Nama}
                      className="w-32 h-32 md:w-48 md:h-48 rounded-lg object-cover mb-4"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        if (target.nextSibling) {
                          (target.nextSibling as HTMLElement).style.display = 'flex';
                        }
                      }}
                    />
                  ) : null}
                  <div 
                    className="w-32 h-32 md:w-48 md:h-48 rounded-lg bg-blue-500 flex items-center justify-center text-white text-5xl md:text-6xl font-bold mb-4"
                    style={{ display: employee.Foto ? 'none' : 'flex' }}
                  >
                    {employee.Nama.charAt(0).toUpperCase()}
                  </div>
                  
                  <span className={`px-4 py-2 inline-flex text-sm font-semibold rounded-full ${getStatusBadgeColor(employee.Status_Pekerjaan)}`}>
                    {employee.Status_Pekerjaan.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>

            {/* Information Cards - Takes 2 columns, arranged in 2-column grid */}
            <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 auto-rows-min">
              {/* Personal Information */}
              <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
                <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">Personal Information</h2>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">NIP (Employee ID)</label>
                    <p className="mt-1 text-gray-900">{employee.NIP || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Full Name</label>
                    <p className="mt-1 text-gray-900">{employee.Nama}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Phone Number</label>
                    <p className="mt-1 text-gray-900">{employee.No_Telp || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Date of Birth</label>
                    <p className="mt-1 text-gray-900">{formatDate(employee.Tanggal_Lahir)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Gender</label>
                    <p className="mt-1 text-gray-900">{employee.Jenis_Kelamin || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">National ID (KTP)</label>
                    <p className="mt-1 text-gray-900">{employee.KTP || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Marital Status</label>
                    <p className="mt-1 text-gray-900">{employee.Status_Kawin || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Employment Information */}
              <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
                <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">Employment Information</h2>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Title</label>
                    <p className="mt-1 text-gray-900">{employee.Jabatan || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Employment Status</label>
                    <p className="mt-1">
                      <span className={`px-3 py-1 inline-flex text-xs md:text-sm font-semibold rounded-full ${getStatusBadgeColor(employee.Status_Pekerjaan)}`}>
                        {employee.Status_Pekerjaan.toUpperCase()}
                      </span>
                    </p>
                  </div>
                  {employee.company && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Company</label>
                      <p className="mt-1 text-gray-900">{employee.company.nama}</p>
                      {employee.company.alamat && (
                        <p className="mt-1 text-sm text-gray-600">{employee.company.alamat}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Address Information */}
              <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
                <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">Address</h2>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Province</label>
                    <p className="mt-1 text-gray-900">{employee.Provinsi || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">City/Regency</label>
                    <p className="mt-1 text-gray-900">{employee.Kota || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">District</label>
                    <p className="mt-1 text-gray-900">{employee.Kecamatan || '-'}</p>
                  </div>
                </div>
              </div>
              
              {/* Bound Devices */}
              <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
                <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">Bound Devices</h2>
                {employee.devices && employee.devices.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {employee.devices.map((device) => (
                      <div key={device.id} className="border border-gray-200 rounded-lg p-3">
                        <div className="space-y-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-500">Device Name</label>
                            <p className="mt-1 text-sm text-gray-900 break-words">{device.name || device.id}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500">MAC Address</label>
                            <p className="mt-1 text-xs text-gray-900 font-mono break-all">{device.mac || '-'}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500">Status</label>
                            <p className="mt-1">
                              <span className={`px-2 py-1 inline-flex text-xs font-semibold rounded-full ${
                                device.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {device.status}
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">No devices bound to this employee</p>
                )}
              </div>

              {/* Emergency Contact */}
              {employee.Kontak_Darurat && (
                <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
                  <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">Emergency Contact</h2>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Name</label>
                      <p className="mt-1 text-gray-900">{employee.Kontak_Darurat.name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Relationship</label>
                      <p className="mt-1 text-gray-900">{employee.Kontak_Darurat.relationship}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Phone Number</label>
                      <p className="mt-1 text-gray-900">{employee.Kontak_Darurat.phone}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Address</label>
                      <p className="mt-1 text-gray-900">{employee.Kontak_Darurat.address}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 md:col-span-2">
                <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">Record Information</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Created</label>
                    <p className="mt-1 text-gray-900">{formatDate(employee.date_created)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Last Updated</label>
                    <p className="mt-1 text-gray-900">{formatDate(employee.date_updated)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="ml-4 text-lg font-semibold text-gray-900">Delete Employee</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <strong>{employee.Nama}</strong>? This action cannot be undone. 
              All devices bound to this employee will be unbound.
            </p>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleDeleteCancel}
                disabled={deleting}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeDetail;
